import { t } from '@if/core/i18n'
import { ECarCondition, EPlanType, EWorksheetDecision, getECarConditionList } from '@if/core/types'
import * as yup from '@if/core/yup'
import {
  ApplicableTax,
  BaseWorksheet,
  ComputedTax,
  baseWorksheetFundingThresholdExtendSchema,
  baseWorksheetSchema,
} from './BaseWorksheetSchema'
import { ParameterizedMessage } from './ParameterizedMessage'
import { EExtendedServiceDuration, getEExtendedServiceDurationList } from './constants'

const additionsSchema = {
  code: yup.string().default(''),
  description: yup.string().default(''),
}

export const AdditionsSchema = yup.object(additionsSchema)
export type Additions = yup.InferType<typeof AdditionsSchema>
const PriceRangeSchema = yup.object().shape({
  xclean: yup.number().default(0),
  clean: yup.number().required().default(0),
  avg: yup.number().required().default(0),
  rough: yup.number().required().default(0),
})

const PricingSchema = yup.object().shape({
  baseRetailPrices: PriceRangeSchema,
  adjustedRetailPrices: PriceRangeSchema,
  baseWholePrices: PriceRangeSchema,
  adjustedWholePrices: PriceRangeSchema,
  regionalRetailAdjustments: PriceRangeSchema,
  regionalWholeAdjustments: PriceRangeSchema,
  kilometerThresholds: PriceRangeSchema,
})
const vehicleSchema = {
  thirdPartyId: yup.string().nullable().default(null),
  isTradeIn: yup.boolean().default(false),
  pricingCategory: yup.string().nullable(),

  // Common
  year: yup
    .integer()
    .positive()
    .default(0)
    .when('vin', {
      is: (value: string) => value && value.length === 17,
      then: (schema) => schema.required(),
    }),
  make: yup
    .string()
    .default('')
    .when('vin', {
      is: (value: string) => value && value.length === 17,
      then: (schema) => schema.required(),
    }),
  model: yup
    .string()
    .default('')
    .when('vin', {
      is: (value: string) => value && value.length === 17,
      then: (schema) => schema.required(),
    }),
  // New vehicle
  msrp: yup.double().when(['isTradeIn', 'condition'], {
    is: (isTradeIn: boolean, condition: ECarCondition) => !isTradeIn && condition && condition === ECarCondition.New,
    then: (schema) => schema.required().positive(),
  }),

  // Vehicle
  additions: yup.array(AdditionsSchema).default([]),
  includes: yup.array(yup.string().required().default('')).default([]),
  series: yup
    .string()
    .default('')
    .when('isTradeIn', {
      is: false,
      then: (schema) => schema.required(),
    }),
  condition: yup
    .mixed<ECarCondition>()
    .default('' as ECarCondition)
    .nullable()
    .oneOf(getECarConditionList())
    .when('isTradeIn', {
      is: false,
      then: (schema) => schema.required(),
    }),
  vin: yup
    .string()
    .default('')
    .uppercase()
    .when('isTradeIn', {
      is: false,
      then: (schema) =>
        schema.required().matches(/^[A-HJ-NPR-Za-hj-npr-z0-9]{17}$/, () => t('common.errors.invalidVin')), // Same as server
    }),
  mileage: yup
    .integer()
    .positive()
    .default(0)
    .when('isTradeIn', {
      is: false,
      then: (schema) => schema.required(),
    }),
  bodyStyle: yup
    .string()
    .default('')
    .when('isTradeIn', {
      is: false,
      then: (schema) => schema.required(),
    }),
  pricing: PricingSchema,
  // Trade In Vehicle
  allowance: yup
    .double()
    .default(0)
    .when(['isTradeIn'], {
      is: true,
      then: (schema) => schema.required(),
    }),
  lienAmount: yup.double().positive(),
  balanceOwedTo: yup.string().when(['isTradeIn', 'vin'], {
    is: (isTradeIn: boolean, lienAmount: number | null) => lienAmount && isTradeIn,
    then: (schema) => schema.required(),
  }),
  netTradeInAllowance: yup.double(),
  thirdPartySearchOverride: yup.boolean().default(false),
  thirdPartyEvaluationValue: yup.double().nullable().default(null),
  brokerEstimatedValue: yup.double().nullable().default(null),
}

export const VehicleSchema = yup.object(vehicleSchema)
export type Vehicle = yup.InferType<typeof VehicleSchema>

export const vehicleInternalEvaluation = yup.object({
  id: yup.string().nullable(),
  vehicleValue: yup.double().positive(),
  discretionaryAllocation: yup.double().positive().max(500).default(0),
})

export type VehicleInternalEvaluation = yup.InferType<typeof vehicleInternalEvaluation>

export const vehicleEvaluationDTO = yup.object({
  creditApplicationId: yup.string().nullable(),
  vehicleValue: yup.double().positive(),
  discretionaryAllocation: yup.double().min(0).max(500).default(0),
  carfaxValue: yup.double().nullable().positive().default(0),
  versionTag: yup.string(),
})

export type VehicleEvaluationDTO = yup.InferType<typeof vehicleEvaluationDTO>

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const autoWorksheetDecisionDTOSchema = yup.object({
  creditApplicationId: yup.string().nullable(),
  otherReason: yup.string().default('').nullable(),
  decision: yup.mixed<EWorksheetDecision>(),
  fundingThresholdExtend: baseWorksheetFundingThresholdExtendSchema
    .notRequired()
    .nullable()
    .default(baseWorksheetFundingThresholdExtendSchema.getDefault()),
  refusalMessages: yup.mixed<ParameterizedMessage[]>().default([]),
  versionTag: yup.string(),
})

export type AutoWorksheetDecisionDTO = yup.InferType<typeof autoWorksheetDecisionDTOSchema>

export const AutoWorksheetSchema = yup
  .object({
    vehicle: VehicleSchema,
    tradeInVehicle: VehicleSchema.nullable().default(null),
    carfaxValue: yup.double().nullable().positive().default(0),
    price: yup.double().positive().required(),
    rebate: yup.double().positive(),
    cashDown: yup.double().positive(),
    installationDelivery: yup.double().positive(),
    licenseFee: yup.double().positive(),
    dealerAdminFee: yup.double().positive(),
    otherTaxable: yup.double().positive(),
    otherTaxableDescription: yup
      .string()
      .nullable()
      .when('otherTaxable', {
        is: (otherTaxable: number | null) => otherTaxable !== null,
        then: (schema) => schema.required(),
      }),
    otherNonTaxable: yup.double().positive(),
    otherNonTaxableDescription: yup
      .string()
      .nullable()
      .when('otherNonTaxable', {
        is: (otherNonTaxable: number | null) => otherNonTaxable !== null,
        then: (schema) => schema.required(),
      }),
    icebergPPSA: yup.integer().positive(),
    vehicleAccAndFees: yup.double(),
    lifeInsurance: yup.double().positive(),
    accidentAndHealthInsurance: yup.double().positive(),
    isTaxExempt: yup.boolean().default(false),
  })
  .shape(
    {
      extendedService: yup.double().when('extendedServiceDuration', {
        is: (extendedServiceDuration: number | null) => extendedServiceDuration !== null,
        then: (schema) => schema.required().positive(),
      }),
      extendedServiceDuration: yup
        .numberEnum<EExtendedServiceDuration>(getEExtendedServiceDurationList())
        .when('extendedService', {
          is: (extendedService: number | null) => extendedService !== null,
          then: (schema) => schema.required(),
        }),
      gapInsurance: yup.double().when('gapInsuranceDuration', {
        is: (gapInsuranceDuration: number | null) => gapInsuranceDuration !== null,
        then: (schema) => schema.required().positive(),
      }),
      gapInsuranceDuration: yup.integer().when('gapInsurance', {
        is: (gapInsurance: number | null) => gapInsurance !== null,
        then: (schema) => schema.required().moreThan(-1, () => t('common.errors.positive')),
      }),
    },
    [
      ['extendedService', 'extendedServiceDuration'],
      ['gapInsurance', 'gapInsuranceDuration'],
    ],
  )
  .concat(baseWorksheetSchema)

export type AutoWorksheet = yup.InferType<typeof AutoWorksheetSchema> &
  BaseWorksheet & {
    applicableTaxesOnVehicleAndFees: ApplicableTax[]
    applicableTaxOnInsurances: ApplicableTax
    computedVehicleAndFeesTaxes: ComputedTax[]
    tradeInVehicleTaxedAmount: number
    extendedServiceTaxedAmount: number
    brokerRequestedFinancingPlan: EPlanType
    totalAmountFinancedWithoutInsurancesAndExtendedWarranty: number
    vehicleEvaluation: VehicleInternalEvaluation
    thirdPartyVehicleValue: number
    finalVehicleValue: number
    margin: number
    authorizedSurplus: number
    authorizedAmount: number
    taxesOnInsurance: number
    aftermarketVsValue: number
  }

export type AutoWorksheetRevision = AutoWorksheet & {
  CurrentRevisionId: string
}
